import { useEffect, useState } from "react";
import { unitById, type Role } from "@zend/content";
import { useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { onWs } from "../api/ws.js";
import { UNITS_TABS, useUnits, type UnitsTab } from "./state.js";
import { UnitCard } from "./UnitCard.js";
import { UnitDetail } from "./UnitDetail.js";
import { Formation } from "./Formation.js";
import { Workshop } from "./Workshop.js";
import { ArmoryTab, CollectionTab, RepairTab } from "./Depot.js";
import { Tabs } from "./shared.js";
import { RoleIcon, StatIcon } from "../assets/gameIcons.js";
import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import "./formation.css";

const ROLES: (Role | "all")[] = ["all", "commander", "defender", "attacker", "saboteur", "support"];

/** The Units screen: roster, formation, workshop, repair, armory, collection (30-units.md). */
export function UnitsView({ route }: { route: string }) {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const selected = useUnits((s) => s.selected);
  const squadSlot = useUnits(s => s.squadSlot);
  const select = useUnits((s) => s.select);
  const refreshRoster = useUnits((s) => s.refreshRoster);
  const [role, setRole] = useState<Role | "all">("all");
  const tabFromRoute = route.split("/")[3] as UnitsTab | undefined;
  const tab: UnitsTab = tabFromRoute && UNITS_TABS.includes(tabFromRoute) ? tabFromRoute : "roster";

  useEffect(() => {
    void refreshRoster();
    const offs = ["repair.done", "craft.done", "workshop.pull", "battle.result", "event.start", "event.end", "event.extended", "day.new"].map((k) => onWs(k, () => void refreshRoster()));
    return () => offs.forEach((off) => off());
  }, [refreshRoster]);

  const squad = roster?.squads.find(value => value.slot === squadSlot);
  const filled = Object.values(squad?.members ?? {}).filter(Boolean).length;
  const units = (roster?.units ?? []).filter((u) => role === "all" || unitById(u.unitId).role === role).sort((a, b) => b.power - a.power);

  return (
    <div className="units-view" data-testid="units-view">
      <Tabs tabs={UNITS_TABS} active={tab} onChange={(next) => navigate(next === "roster" ? "/app/units" : `/app/units/${next}`)} label={(x) => t(`units.tab.${x}`)} testPrefix="units-tab" />
      {tab === "roster" ? (
        <>
          <section className="squad-entry" data-testid="squad-entry">
            <div className="squad-entry-copy"><h2>{t("squad.entryTitle")}</h2>{roster ? <span>{t("units.squad.n", { n: squadSlot })}{squad?.name ? ` · ${squad.name}` : ""} · {t("squad.filled", { n: filled })}{squad ? <> · <StatIcon stat="power" size={17} /> {squad.power.toLocaleString()}</> : null}</span> : null}</div>
            <Button variant="gold" onClick={() => navigate("/app/units/squads")} data-testid="squad-edit"><Icon name="people" />{t(filled ? "squad.edit" : "squad.create")}</Button>
          </section>
          <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {ROLES.map((r) => (
              <button key={r} type="button" className={`chip ${role === r ? "ok" : ""}`} onClick={() => setRole(r)} data-testid={`role-${r}`}>
                {r === "all" ? t("units.all") : <><RoleIcon role={r} size={18} /> {t(`role.${r}`)}</>}
              </button>
            ))}
            <span className="grow" />
            {roster ? <span className="badge gold">{t("units.count", { n: roster.units.length })}</span> : null}
          </div>
          {!roster ? <p className="dim">{t("common.loading")}</p> : null}
          <div className="unit-grid" data-testid="roster-grid">
            {units.map((u) => (
              <UnitCard key={u.id} unit={u} selected={selected === u.id} onClick={() => select(u.id)} hint={u.squad !== null ? t("units.inSquad", { n: u.squad }) : undefined} />
            ))}
          </div>
        </>
      ) : null}
      {tab === "squads" ? <Formation /> : null}
      {tab === "workshop" ? <Workshop /> : null}
      {tab === "repair" ? <RepairTab /> : null}
      {tab === "armory" ? <ArmoryTab /> : null}
      {tab === "collection" ? <CollectionTab /> : null}
      {selected ? <UnitDetail rowId={selected} /> : null}
    </div>
  );
}
